import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { DocumentTextIcon, TrashIcon } from "@heroicons/react/outline"
import { getSupplierInvoices, attachSupplierInvoiceFile, deleteSupplierInvoiceFile } from "../../services"
import { FileInputField } from "../../components/FileInputField"

const I18N_BASE = "replenishments.show_replenishment_boxes.supplier_invoice_files"
// Poll so a document uploaded in another receiving window shows up here (and is
// never lost when whoever finishes the replenishment does so). State is
// server-authoritative — every window converges on the same list within the interval.
const POLL_INTERVAL_MS = 4000

const formatBytes = (bytes) => {
  if (!bytes) return ""
  const kb = bytes / 1024
  return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`
}

// Loose receipt documents for a replenishment (any file type): upload via the
// click-or-drag dropzone (FileInputField, same UX as order-creation documents),
// list, delete. Shares the emerald "docto" identity of SupplierInvoiceChip.
// Rendered only when the warehouse has supplier-invoice capture enabled.
export const SupplierInvoiceFiles = ({ replenishmentId }) => {
  const { i18n } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [errMsg, setErrMsg] = useState(null)
  // react-files reports its ACCUMULATED list on every change — remember what we
  // already sent so a new drop doesn't re-upload the earlier files.
  const uploadedIdsRef = useRef(new Set())

  const { data, refetch } = useQuery(
    ["supplier-invoices", replenishmentId],
    () => getSupplierInvoices(replenishmentId),
    { refetchInterval: POLL_INTERVAL_MS, refetchOnWindowFocus: true }
  )

  const files = data?.invoice_files || []

  const handleUpload = async (selected) => {
    const fresh = (selected || []).filter((file) => !uploadedIdsRef.current.has(file.id))
    if (fresh.length === 0) return
    fresh.forEach((file) => uploadedIdsRef.current.add(file.id))

    setErrMsg(null)
    setBusy(true)
    try {
      for (const file of fresh) {
        await attachSupplierInvoiceFile(replenishmentId, file)
      }
      await refetch()
    } catch (error) {
      setErrMsg(i18n.t(`${I18N_BASE}.upload_error`))
    } finally {
      setBusy(false)
    }
  }

  const handleDelete = async (attachmentId) => {
    setErrMsg(null)
    setBusy(true)
    try {
      await deleteSupplierInvoiceFile(replenishmentId, attachmentId)
      await refetch()
    } catch (error) {
      setErrMsg(i18n.t(`${I18N_BASE}.delete_error`))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="bg-white p-5">
      <h3 className="font-bold text-lg flex items-center gap-2">
        <DocumentTextIcon className="h-5 w-5 text-emerald-600 flex-shrink-0" aria-hidden="true" />
        {i18n.t(`${I18N_BASE}.title`)}
      </h3>
      <p className="text-gray-600 text-sm mt-1">{i18n.t(`${I18N_BASE}.description`)}</p>

      {files.length > 0 && (
        <ul className="my-3 divide-y divide-gray-100 border border-gray-100 rounded-lg">
          {files.map((file) => (
            <li key={file.id} className="flex items-center justify-between gap-2 px-3">
              <a
                href={`${process.env.REACT_APP_PLATFORM_URL}${file.url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 min-h-touch flex-1 min-w-0 text-emerald-700 hover:underline focus:outline-none focus:ring-2 focus:ring-emerald-300 rounded"
              >
                <DocumentTextIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                <span className="truncate font-medium">{file.filename}</span>
                {file.byte_size ? (
                  <span className="text-gray-500 text-xs flex-shrink-0">{formatBytes(file.byte_size)}</span>
                ) : null}
              </a>
              <button
                type="button"
                data-testid={`supplier-invoice-file-delete-${file.id}`}
                onClick={() => handleDelete(file.id)}
                disabled={busy}
                aria-label={i18n.t(`${I18N_BASE}.delete`)}
                className="min-h-touch px-3 inline-flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-300 rounded disabled:opacity-50 transition-colors flex-shrink-0"
              >
                <TrashIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <FileInputField
        containerClassName="mt-3"
        placeholder={i18n.t(`${I18N_BASE}.drop_placeholder`)}
        onChange={handleUpload}
        onError={() => setErrMsg(i18n.t(`${I18N_BASE}.upload_error`))}
        accepts={null}
        maxFileSize={Infinity}
      />
      {busy && (
        <div className="text-gray-500 text-sm mt-2">{i18n.t(`${I18N_BASE}.uploading`)}</div>
      )}

      {errMsg && (
        <div className="font-bold text-red-500 mt-2" data-testid="supplier-invoice-file-error">{errMsg}</div>
      )}
    </section>
  )
}
